import React, { useEffect, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { adminService } from '../../../../services/adminService';
import { toast } from 'sonner';

const FIELDS = [
  {
    key: 'sell_through_target_pct',
    label: 'Sell-through target (%)',
    hint: 'At or above this is Healthy',
  },
  {
    key: 'sell_through_watch_pct',
    label: 'Watch band floor (%)',
    hint: 'Between this and the target is Watch; below it is At risk',
  },
  {
    key: 'min_contribution_margin_pct',
    label: 'Minimum contribution margin (%)',
    hint: 'Below this is flagged on its own',
  },
  {
    key: 'min_revenue_floor',
    label: 'Minimum revenue floor ($)',
    hint: 'Clients under this are excluded from ranking as too small to judge',
  },
];

export const ThresholdsDialog = ({ open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    adminService.getClientPerformanceThresholds()
      .then((d) => setForm({
        sell_through_target_pct: d.sell_through_target_pct,
        sell_through_watch_pct: d.sell_through_watch_pct,
        min_contribution_margin_pct: d.min_contribution_margin_pct,
        min_revenue_floor: d.min_revenue_floor,
      }))
      .catch(() => toast.error('Failed to load thresholds'));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, Number(v)]),
      );
      await adminService.updateClientPerformanceThresholds(payload);
      toast.success('Thresholds saved');
      onSaved?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save thresholds');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="thresholds-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-amber-500" /> Performance thresholds
          </DialogTitle>
          <DialogDescription>
            These bands drive every Healthy / Watch / At risk call and the recommendations.
          </DialogDescription>
        </DialogHeader>

        {form && (
          <div className="space-y-4 py-2">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <Label htmlFor={f.key}>{f.label}</Label>
                <Input
                  id={f.key}
                  type="number"
                  step="0.1"
                  value={form[f.key] ?? ''}
                  onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                  className="mt-1 min-h-[44px]"
                  data-testid={`threshold-${f.key}`}
                />
                <p className="text-xs text-gray-500 mt-1">{f.hint}</p>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            className="bg-amber-500 hover:bg-amber-600"
            onClick={save}
            disabled={saving || !form}
            data-testid="save-thresholds-btn"
          >
            {saving ? 'Saving…' : 'Save thresholds'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ThresholdsDialog;
